import React from 'react';
import { ORGAN_QA_LABELS } from '../../clients/viewer';
import { useSeriesLabels } from '../../hooks/useSeriesLabels';

const QA_LABELS = new Set<string>(Object.values(ORGAN_QA_LABELS));

export interface QACheckItemProps {
  instance?: { Modality?: string; StudyInstanceUID?: string; SeriesInstanceUID?: string };
  customization?: { color?: string };
}

/**
 * D6 -- a component, not a contentF body; CursorCoordinatesItem carries the full reason.
 *
 * D8, confirmed with the user 2026-08-13: the screen text is develop's, unchanged. The organ
 * labels render RAW ("QAPancreas", not "Pancreas"), `Labels:` is a literal, and neither is
 * translated -- M5 owns the 'es' default. This is a reviewer-facing screen whose wording is
 * product behaviour, not migration latitude.
 *
 * §5.6: the item mounts unconditionally for EVERY role. That is confirmed and deliberate
 * (parent :1010-1020) -- every role sees the reviewer's name and the reviewed organs, and
 * QACheck is one of three condition-less entries in its array, not an isolated oversight.
 *
 * The CT gate is develop's (its effect returned early on a non-CT instance,
 * CustomizableViewportOverlay.tsx:585-590 there). It is expressed by WITHHOLDING the UIDs
 * rather than by an early return, because a hook cannot be called conditionally.
 */
export default function QACheckItem({ instance, customization }: QACheckItemProps) {
  const isCt = instance?.Modality === 'CT';

  const entry = useSeriesLabels(
    isCt ? instance?.StudyInstanceUID : undefined,
    isCt ? instance?.SeriesInstanceUID : undefined
  );

  // Filtered HERE rather than in the service, so the cache stays faithful to what Orthanc
  // sent -- M4.3's download row needs the same series' labels and should not inherit this
  // item's vocabulary.
  const reviewedOrgans = entry?.labels.filter(label => QA_LABELS.has(label)) ?? [];

  // §5.6: nothing at all when there is no cache entry yet, or no surviving label -- so a
  // series labelled only with non-QA labels shows nothing, exactly as develop did.
  if (!entry || reviewedOrgans.length === 0) {
    return null;
  }

  return (
    <div
      className="overlay-item flex flex-col"
      style={{ color: customization?.color }}
    >
      {entry.reviewerName ? (
        <div>
          <span>QA - {entry.reviewerName}</span>
        </div>
      ) : null}

      <div>Labels:</div>
      <ul className="ml-1">
        {reviewedOrgans.map(label => (
          <li
            key={label}
            className="shrink-0"
          >
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
